// src/api/localGameBackend.js
// Serverless Game Engine for EvidenceApp (Firebase Hosting + Cloud Firestore REST + localStorage fallback)

const FIRESTORE_BASE_URL =
  'https://firestore.googleapis.com/v1/projects/pixelings-studio/databases/(default)/documents/sessions';
const LOCAL_STORAGE_PREFIX = 'evapp_session_';

const SessionStatus = {
  Created: 'created',
  Started: 'started',
  Gameover: 'gameover',
  Victory: 'victory',
};

let casesIndexCache = null;
const cardSetCache = {};

async function loadCasesIndex() {
  if (casesIndexCache) return casesIndexCache;
  const res = await fetch('/cases/index.json');
  if (!res.ok) {
    throw new Error('Failed to load cases index.');
  }
  casesIndexCache = await res.json();
  return casesIndexCache;
}

async function getAvailableLanguages(caseCode) {
  if (!caseCode) return [];
  const codeUpper = caseCode.toUpperCase();
  const index = await loadCasesIndex();
  const langs = index[codeUpper];
  if (!langs) return [];
  return Array.isArray(langs) ? [...langs] : [langs];
}

async function getCardSet(caseCode, language) {
  const key = `${caseCode.toUpperCase()}-${language.toUpperCase()}`;
  if (cardSetCache[key]) return cardSetCache[key];

  const res = await fetch(`/cases/${key}.json`);
  if (!res.ok) {
    const err = new Error(`Card data not found for ${key}`);
    err.status = 404;
    throw err;
  }
  const raw = await res.json();
  const cardSet = normalizeCardSet(raw);
  cardSetCache[key] = cardSet;
  return cardSet;
}

function normalizeCardSet(raw) {
  const cards = (raw.Cards || raw.cards || []).map((c) => ({
    CardId: c.CardId ?? c.cardId ?? '',
    Type: c.Type ?? c.type ?? '',
    Category: c.Category ?? c.category ?? '',
    SubCategory: c.SubCategory ?? c.subCategory ?? '',
    CategoryLabel: c.CategoryLabel ?? c.categoryLabel ?? '',
    Title: c.Title ?? c.title ?? '',
    Subtitle: c.Subtitle ?? c.subtitle ?? '',
    FieldType: c.FieldType ?? c.fieldType ?? '',
    FieldValue: c.FieldValue ?? c.fieldValue ?? '',
    Cost: Number(c.Cost ?? c.cost ?? 0),
    IsSolution: Boolean(c.IsSolution ?? c.isSolution ?? false),
    Modifiers: c.Modifiers ?? c.modifiers ?? [],
    ModifiersLabel: c.ModifiersLabel ?? c.modifiersLabel ?? '',
    Goals: c.Goals ?? c.goals ?? [],
    GoalsLabel: c.GoalsLabel ?? c.goalsLabel ?? '',
    ...(c.Image || c.image ? { Image: c.Image ?? c.image } : {}),
  }));

  const missionBase = cards.find(
    (x) => (x.Type || '').toLowerCase() === 'mission'
  );
  const modifiers = missionBase?.Modifiers || [];
  const intervalMod = modifiers.find((m) => m.startsWith('interval_'));
  const interval = intervalMod
    ? parseInt(intervalMod.slice(9).replace(/s$/i, ''), 10) || 30
    : 30;

  const mission = missionBase
    ? {
        ...missionBase,
        Cost: Math.trunc(missionBase.Cost),
        IsOneShot: modifiers.includes('one_shot'),
        IsHurryUp: modifiers.includes('hurry_up'),
        Interval: interval,
        Card: missionBase,
      }
    : null;

  return {
    Name: raw.Name ?? raw.name ?? '',
    CaseCode: raw.CaseCode ?? raw.caseCode ?? '',
    CaseId: raw.CaseId ?? raw.caseId ?? 0,
    Language: raw.Language ?? raw.language ?? '',
    Cards: cards,
    SolutionCards: cards
      .filter((w) => w.IsSolution)
      .sort((a, b) => a.CardId.localeCompare(b.CardId)),
    StoryCards: cards.filter((w) => w.Type === 'story'),
    Mission: mission,
    Penalty: raw.Penalty ?? raw.penalty ?? null,
    Ui: raw.Ui || raw.ui || 'countdown',
  };
}

function generateSessionCode(size = 6) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < size; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// Evaluate Reverse Polish Notation (RPN) expressions such as "$0 2 / 12 max" or "4"
function evaluateRpn(expr, arg0) {
  if (!expr || !expr.trim()) return 0;
  const tokens = expr.trim().split(/\s+/);
  const stack = [];

  for (const token of tokens) {
    if (token === '$0') {
      stack.push(Number(arg0));
    } else if (/^-?\d+(\.\d+)?$/.test(token)) {
      stack.push(Number(token));
    } else {
      const b = stack.pop() ?? 0;
      const a = stack.pop() ?? 0;
      switch (token.toLowerCase()) {
        case '+':
          stack.push(a + b);
          break;
        case '-':
          stack.push(a - b);
          break;
        case '*':
          stack.push(a * b);
          break;
        case '/':
          stack.push(b !== 0 ? Math.trunc(a / b) : 0);
          break;
        case 'max':
          stack.push(Math.max(a, b));
          break;
        case 'min':
          stack.push(Math.min(a, b));
          break;
        default:
          break;
      }
    }
  }
  return Math.trunc(stack.pop() ?? 0);
}

function sessionToFirestoreFields(session) {
  return {
     fields: {
      SessionId: { stringValue: String(session.SessionId || '') },
      Code: { stringValue: String(session.Code || '') },
      PlayedCards: {
        arrayValue: {
          values: (session.PlayedCards || []).map((c) => ({ stringValue: String(c) })),
        },
      },
      Status: { stringValue: String(session.Status || '') },
      Counter: { integerValue: String(Math.trunc(session.Counter || 0)) },
      HurryUp: { stringValue: session.HurryUp ? String(session.HurryUp) : '' },
      Interval: { integerValue: String(Math.trunc(session.Interval || 0)) },
      Penalty: { integerValue: String(Math.trunc(session.Penalty || 0)) },
      UpdatedAt: { stringValue: new Date().toISOString() },
    },
  };
}

function firestoreFieldsToSession(doc) {
  if (!doc || !doc.fields) return null;
  const f = doc.fields;
  const played =
    f.PlayedCards?.arrayValue?.values?.map((v) => v.stringValue) || [];
  return {
    SessionId: f.SessionId?.stringValue || '',
    Code: f.Code?.stringValue || '',
    PlayedCards: played,
    Status: f.Status?.stringValue || SessionStatus.Created,
    Counter: parseInt(f.Counter?.integerValue || '0', 10),
    HurryUp: f.HurryUp?.stringValue || null,
    Interval: parseInt(f.Interval?.integerValue || '0', 10),
    Penalty: parseInt(f.Penalty?.integerValue || '0', 10),
  };
}

async function getSession(sessionId) {
  if (!sessionId) return null;
  const id = sessionId.toUpperCase();

  try {
    const res = await fetch(`${FIRESTORE_BASE_URL}/${encodeURIComponent(id)}`);
    if (res.ok) {
      const doc = await res.json();
      const session = firestoreFieldsToSession(doc);
      if (session) {
        try {
          localStorage.setItem(LOCAL_STORAGE_PREFIX + id, JSON.stringify(session));
        } catch (_) {}
        return session;
      }
    }
  } catch (err) {
    console.warn('Firestore read fallback to localStorage:', err);
  }

  try {
    const localRaw = localStorage.getItem(LOCAL_STORAGE_PREFIX + id);
    if (localRaw) {
      return JSON.parse(localRaw);
    }
  } catch (_) {}

  return null;
}

async function saveSession(session) {
  const id = session.SessionId.toUpperCase();
  session.SessionId = id;

  try {
    localStorage.setItem(LOCAL_STORAGE_PREFIX + id, JSON.stringify(session));
  } catch (_) {}

  try {
    await fetch(`${FIRESTORE_BASE_URL}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sessionToFirestoreFields(session)),
    });
  } catch (err) {
    console.warn('Firestore save warning (saved in localStorage):', err);
  }

  return session;
}

async function getCards(language, session) {
  const fullCardSet = await getCardSet(session.Code, language);
  const playedCards = [];
  for (const id of session.PlayedCards || []) {
    const card = fullCardSet.Cards.find(
      (c) => c.CardId.toLowerCase() === id.toLowerCase()
    );
    if (card) {
      playedCards.push(card);
    }
  }
  return { fullCardSet, playedCards };
}

function updateCounter(session, playedCards, penaltyRpn = null) {
  let newCounter = 0;
  for (const playedCard of playedCards) {
    newCounter += Math.trunc(playedCard.Cost || 0);
  }

  if (session.Interval > 0 && session.HurryUp) {
    const now = Date.now();
    const start = new Date(session.HurryUp).getTime();
    if (!isNaN(start) && now > start) {
      const elapsedSeconds = (now - start) / 1000;
      newCounter -= Math.floor(elapsedSeconds / session.Interval);
    }
  }

  if (penaltyRpn && penaltyRpn.trim() !== '') {
    const penalty = evaluateRpn(penaltyRpn, newCounter - (session.Penalty || 0));
    if (penalty > 0) {
      session.Penalty = (session.Penalty || 0) + penalty;
    }
  }

  session.Counter = newCounter - (session.Penalty || 0);
  return newCounter;
}

async function checkAndSetGameOver(session, fullCardSet, playedCards) {
  if (session.Status === SessionStatus.Gameover) {
    return true;
  }

  if (session.Counter <= 0) {
    session.Status = SessionStatus.Gameover;
    const defeatCard = fullCardSet.StoryCards.find(
      (w) =>
        w.Category === 'defeat' &&
        (w.SubCategory === 'timeup' || !w.SubCategory)
    );
    if (defeatCard) {
      playedCards.push(defeatCard);
      session.PlayedCards.push(defeatCard.CardId);
    }
    await saveSession(session);
    return true;
  }

  return false;
}

function startGame(session, fullCardSet) {
  if ((session.Status || '').toLowerCase() === SessionStatus.Created) {
    session.Status = SessionStatus.Started;
    const missionCard = fullCardSet.Mission;
    if (missionCard && missionCard.IsHurryUp) {
      session.HurryUp = new Date().toISOString();
      session.Interval = missionCard.Interval;
    }
    return true;
  }
  return false;
}

function checkSolution(fullCardSet, guessCards) {
  const orderedGuess = [...guessCards].map((c) => c.toUpperCase()).sort();
  const orderedSolution = fullCardSet.SolutionCards.map((s) =>
    s.CardId.toUpperCase()
  ).sort();

  const isEqual =
    orderedGuess.length === orderedSolution.length &&
    orderedGuess.every((val, idx) => val === orderedSolution[idx]);

  if (isEqual) {
    const victoryCard = fullCardSet.StoryCards.find(
      (w) =>
        w.Category === 'victory' &&
        (w.SubCategory === 'correct' || !w.SubCategory)
    );
    return { outcomeCard: victoryCard || null, isSolution: true, gameIsOver: false };
  }

  if (fullCardSet.Mission?.IsOneShot) {
    const defeatCard = fullCardSet.StoryCards.find(
      (w) =>
        w.Category === 'defeat' &&
        (w.SubCategory === 'wrong' || !w.SubCategory)
    );
    return { outcomeCard: defeatCard || null, isSolution: false, gameIsOver: true };
  }

  const tryAgainCard = fullCardSet.StoryCards.find(
    (w) => w.Category === 'tryagain'
  );
  return {
    outcomeCard: tryAgainCard || null,
    isSolution: false,
    gameIsOver: false,
  };
}

export async function handleLocalApiCall(method, path, body) {
  const cleanPath = path.replace(/^\/api/, '');

  // GET /game/validate/{code}
  const validateMatch = cleanPath.match(/^\/game\/validate\/([^/?#]+)/i);
  if (validateMatch && method.toUpperCase() === 'GET') {
    const code = decodeURIComponent(validateMatch[1]).toUpperCase();
    const availableLanguages = await getAvailableLanguages(code);
    if (!availableLanguages || availableLanguages.length === 0) {
      throw new Error(`Code not found: ${code}.`);
    }
    const caseObject = await getCardSet(code, availableLanguages[0]);
    return {
      Languages: availableLanguages,
      Ui: caseObject.Ui || 'countdown',
    };
  }

  // GET /game/session/check/{sessionId}
  const checkMatch = cleanPath.match(/^\/game\/session\/check\/([^/?#]+)/i);
  if (checkMatch && method.toUpperCase() === 'GET') {
    const sId = decodeURIComponent(checkMatch[1]).toUpperCase();
    const session = await getSession(sId);
    if (!session) {
      const err = new Error(`Session '${sId}' not found.`);
      err.status = 404;
      throw err;
    }
    return { Status: session.Status };
  }

  // POST /game/session/create
  if (/^\/game\/session\/create$/i.test(cleanPath) && method.toUpperCase() === 'POST') {
    const { code, language } = body || {};
    if (!code || !language) {
      throw new Error("Please provide 'code' and 'language'.");
    }
    const sessionId = generateSessionCode();
    const cardSet = await getCardSet(code, language);
    const missionCard = cardSet.Mission;

    const sessionToSave = {
      SessionId: sessionId,
      Code: code.toUpperCase(),
      Counter: missionCard.Cost,
      PlayedCards: [missionCard.CardId],
      Status: SessionStatus.Created,
      HurryUp: null,
      Interval: 0,
      Penalty: 0,
    };

    const savedSession = await saveSession(sessionToSave);
    return {
      SessionId: savedSession.SessionId,
      Code: savedSession.Code,
      Language: language,
      Status: savedSession.Status,
      Counter: savedSession.Counter,
      Cards: [missionCard.Card],
    };
  }

  // POST /game/session/read or POST /game/session/join
  if (
    (/^\/game\/session\/(read|join)$/i.test(cleanPath) &&
      method.toUpperCase() === 'POST') ||
    cleanPath.startsWith('/session/status/')
  ) {
    let sessionId = body?.sessionId;
    let language = body?.language;

    const statusMatch = cleanPath.match(/^\/session\/status\/([^/?#]+)/i);
    if (statusMatch) {
      sessionId = decodeURIComponent(statusMatch[1]);
      const qIndex = cleanPath.indexOf('?');
      if (qIndex !== -1) {
        const params = new URLSearchParams(cleanPath.slice(qIndex));
        language = params.get('l') || 'EN';
      }
    }

    if (!sessionId || !language) {
      throw new Error("Please provide 'sessionId' and 'language'.");
    }

    const sessionFromDb = await getSession(sessionId);
    if (!sessionFromDb) {
      throw new Error(`Session '${sessionId}' not found or has expired.`);
    }

    const { fullCardSet, playedCards } = await getCards(language, sessionFromDb);
    updateCounter(sessionFromDb, playedCards);
    await checkAndSetGameOver(sessionFromDb, fullCardSet, playedCards);

    return {
      SessionId: sessionFromDb.SessionId,
      Code: sessionFromDb.Code,
      Language: language,
      Status: sessionFromDb.Status,
      Counter: sessionFromDb.Counter,
      Cards: playedCards,
    };
  }

  // POST /game/card/read
  if (/^\/game\/card\/read$/i.test(cleanPath) && method.toUpperCase() === 'POST') {
    const { sessionId, language } = body || {};
    if (!sessionId || !language) {
      throw new Error("Please provide 'sessionId' and 'language'.");
    }

    const sessionFromDb = await getSession(sessionId);
    if (!sessionFromDb) {
      throw new Error(`Session '${sessionId}' not found or has expired.`);
    }

    const { fullCardSet } = await getCards(language, sessionFromDb);
    const clueMap = {};
    for (const c of fullCardSet.Cards) {
      if (c.Type === 'clue') {
        clueMap[c.CardId] = c.Title;
      }
    }

    return {
      SessionId: sessionFromDb.SessionId,
      Code: sessionFromDb.Code,
      Language: language,
      Cards: clueMap,
    };
  }

  // POST /game/card/play
  if (/^\/game\/card\/play$/i.test(cleanPath) && method.toUpperCase() === 'POST') {
    const { sessionId, cardId, language } = body || {};
    if (!sessionId || !cardId || !language) {
      throw new Error("Please provide 'sessionId', 'cardId', and 'language'.");
    }

    const sessionFromDb = await getSession(sessionId);
    if (!sessionFromDb) {
      throw new Error(`Session '${sessionId}' not found or has expired.`);
    }

    const { fullCardSet, playedCards } = await getCards(language, sessionFromDb);

    updateCounter(sessionFromDb, playedCards);
    let isGameOver = await checkAndSetGameOver(
      sessionFromDb,
      fullCardSet,
      playedCards
    );
    if (isGameOver) {
      return {
        SessionId: sessionFromDb.SessionId,
        Code: sessionFromDb.Code,
        Language: language,
        Status: sessionFromDb.Status,
        Counter: sessionFromDb.Counter,
        Cards: playedCards,
      };
    }

    const playedCard = fullCardSet.Cards.find(
      (c) => c.CardId.toLowerCase() === cardId.toLowerCase()
    );
    if (!playedCard) {
      throw new Error(`Card '${cardId}' not found in the game's card set.`);
    }

    if (
      !sessionFromDb.PlayedCards.some(
        (id) => id.toLowerCase() === playedCard.CardId.toLowerCase()
      )
    ) {
      sessionFromDb.PlayedCards.push(playedCard.CardId);
      playedCards.push(playedCard);
    }

    updateCounter(sessionFromDb, playedCards);
    isGameOver = await checkAndSetGameOver(
      sessionFromDb,
      fullCardSet,
      playedCards
    );
    if (isGameOver) {
      return {
        SessionId: sessionFromDb.SessionId,
        Code: sessionFromDb.Code,
        Language: language,
        Status: sessionFromDb.Status,
        Counter: sessionFromDb.Counter,
        Cards: playedCards,
      };
    }

    startGame(sessionFromDb, fullCardSet);
    const updatedSession = await saveSession(sessionFromDb);

    return {
      SessionId: updatedSession.SessionId,
      Code: updatedSession.Code,
      Language: language,
      Status: updatedSession.Status,
      Counter: updatedSession.Counter,
      Cards: playedCards,
    };
  }

  // POST /game/guess
  if (/^\/game\/guess$/i.test(cleanPath) && method.toUpperCase() === 'POST') {
    const { sessionId, language, cards: guessCards } = body || {};
    if (!sessionId || !language || !Array.isArray(guessCards) || guessCards.length === 0) {
      throw new Error("Please provide 'sessionId', 'cards', and 'language'.");
    }

    const sessionFromDb = await getSession(sessionId);
    if (!sessionFromDb) {
      throw new Error(`Session '${sessionId}' not found or has expired.`);
    }

    const { fullCardSet, playedCards } = await getCards(language, sessionFromDb);

    updateCounter(sessionFromDb, playedCards);
    const isGameOver = await checkAndSetGameOver(
      sessionFromDb,
      fullCardSet,
      playedCards
    );
    if (isGameOver) {
      return {
        SessionId: sessionFromDb.SessionId,
        Code: sessionFromDb.Code,
        Language: language,
        Status: sessionFromDb.Status,
        Counter: sessionFromDb.Counter,
        Cards: playedCards,
      };
    }

    const { outcomeCard, isSolution, gameIsOver } = checkSolution(
      fullCardSet,
      guessCards
    );

    if (isSolution) {
      sessionFromDb.Status = SessionStatus.Victory;
      if (outcomeCard) {
        playedCards.push(outcomeCard);
        sessionFromDb.PlayedCards.push(outcomeCard.CardId);
      }
      await saveSession(sessionFromDb);
      return {
        SessionId: sessionFromDb.SessionId,
        Code: sessionFromDb.Code,
        Language: language,
        Status: sessionFromDb.Status,
        Counter: sessionFromDb.Counter,
        Cards: playedCards,
      };
    } else if (gameIsOver) {
      sessionFromDb.Status = SessionStatus.Gameover;
      if (outcomeCard) {
        playedCards.push(outcomeCard);
        sessionFromDb.PlayedCards.push(outcomeCard.CardId);
      }
      await saveSession(sessionFromDb);
      return {
        SessionId: sessionFromDb.SessionId,
        Code: sessionFromDb.Code,
        Language: language,
        Status: sessionFromDb.Status,
        Counter: sessionFromDb.Counter,
        Cards: playedCards,
      };
    } else {
      let isGameOverAfterPenalty = false;
      if (fullCardSet.Penalty && fullCardSet.Penalty.trim() !== '') {
        updateCounter(sessionFromDb, playedCards, fullCardSet.Penalty);
        isGameOverAfterPenalty = await checkAndSetGameOver(
          sessionFromDb,
          fullCardSet,
          playedCards
        );
        await saveSession(sessionFromDb);
      }

      if (outcomeCard && !isGameOverAfterPenalty) {
        playedCards.push(outcomeCard);
      }

      return {
        SessionId: sessionFromDb.SessionId,
        Code: sessionFromDb.Code,
        Language: language,
        Status: sessionFromDb.Status,
        Counter: sessionFromDb.Counter,
        Cards: playedCards,
      };
    }
  }

  throw new Error(`Unsupported API endpoint: ${method} ${path}`);
}
